import { execAdmin } from '@src/apis/reware/utils/exec';

import {
  parseDeliveryAddress,
  parseImageUrls,
  present,
  type FetchTradeinsFilters,
  type RawTradein,
  type RawTradeinConstants,
  type TradeinsPage,
  type TradeinsResult,
} from './types';

// Cloudinary resize args for line-item thumbnails (same values as src/orders and
// src/inventoryItems — these are the index-page thumbnail dimensions, not reware-admin's).
const THUMBNAIL = { height: 300, width: 300, quality: 80, crop: 'fit' };

const STATE_TRANSITIONS = `
  stateTransitions {
    id
    createdAt
    changeType
    changeFrom
    changeTo
    reason
    note
    changeDetails
    user { name }
  }
`;

const YPACKAGE = `
  id
  state
  stateTransitionAt
  carrier
  carrierService
  trackingNumber
  trackingUrl
  estimatedShippingCost
  actualShippingCost
  length
  width
  height
  weight
  location { id code }
`;

interface FetchTradeinsResponse {
  tradeins: TradeinsPage | null;
}

/**
 * Partner-scoped version of the admin `tradeins` query used by the reware-admin Trade-ins
 * portal. The row selection is a partner-facing *subset* of the admin portal's, not a copy.
 * Deliberately not selected: `kits`, `refund`, `promotionCredits`, `mailerType`,
 * `needsMitiKit`, the `initiated*` story/tag/value display fields, `receivedData`, per-item and
 * per-inventory-item `stateTransitions`, `catalogItem.brand`/`family`/`data`, `ypackage.data`,
 * and `escalations.imageUrls`/`data`.
 *
 * Every filter here must have a matching `$variable` below. A value with no declaration is
 * dropped silently during coercion, so the page comes back unfiltered while the UI still shows
 * the chip — which reads as "everything matched".
 */
export const fetchTradeins = async (filters: FetchTradeinsFilters): Promise<TradeinsResult> => {
  const { partnerId, page, ...rest } = filters;
  const query = `
    query getTradeins(
      $partnerId: Int
      $page: Int
      $pageSize: Int
      $state: String
      $id: Int
      $tradeinSource: String
      $uuidPrefix: String
      $email: String
      $firstName: String
      $lastName: String
      $city: String
      $sendbackState: String
      $hasEscalations: Int
      $height: Int
      $width: Int
      $quality: Int
      $crop: String
    ) {
      tradeins(
        partnerId: $partnerId
        page: $page
        pageSize: $pageSize
        state: $state
        id: $id
        tradeinSource: $tradeinSource
        uuidPrefix: $uuidPrefix
        email: $email
        firstName: $firstName
        lastName: $lastName
        city: $city
        sendbackState: $sendbackState
        hasEscalations: $hasEscalations
      ) {
        count
        page
        pageSize
        rows {
          id
          uuid
          uuidPrefix
          partnerId
          marketMakerPartnerId
          warehouseId
          state
          sendbackState
          tradeinStyle
          initiatedAt
          initiatedCompletionAt
          completedAt
          canceledAt
          deliveryAddress
          countryCode
          transactionCurrencyCode
          tradeinAmount
          shippingCostPaidBySupplier
          initiatedCount
          receivedCount
          initiatedSupplyDescription
          notes
          isSendbackOptIn
          paymentPreference
          cohortGroup
          externalRefId
          canBeApproved
          canBeCanceled
          canSendbackTradein
          canCreateSendbackPack
          canIssueGiftcard
          canSwitchPaymentPreference
          giftcardTotals { total refund manual }
          # lastFour, never number. The Gift cards page masks the full number to ***<last3> on
          # list queries, and the only sanctioned way to read it whole is
          # giftcard.sensitiveDetails, which checks the gift-card permissions and writes a
          # 'revealed' audit record. Reached through tradein.giftcards it is unmasked and
          # unaudited, so selecting it would put a redeemable number for every gift card on
          # every row of every page into the browser.
          giftcards { id amount lastFour source vendorSource state }
          ypackage { ${YPACKAGE} }
          ${STATE_TRANSITIONS}
          escalations {
            id triggerTypeDisplay shipmentTypeDisplay reasonDisplay
            closedReason closedAt createdAt deletedAt warehouseId userId
          }
          tradeinEmailHistory {
            id displayName subjectLine emailType toEmail senderName date state txUrl
          }
          tradeinItems {
            id
            uuid
            state
            stateTransitionAt
            initiatedDepartment
            initiatedCategory
            initiatedCondition
            initiatedQuotedValue
            initiatedQuotedValueLow
            initiatedParentSku
            initiatedColor
            initiatedLikeDescription
            initiatedAuthMethod
            initiatedAuthStatus
            receivedAt
            receivedDepartment
            receivedCategory
            receivedCondition
            receivedNotes
            receivedNeedsCleaning
            receivedNeedsRepair
            receivedRejectedReason
            tradeinPrice
            canBeSupplierSendback
            externalRefId
            catalogItem { id title color parent_sku sku }
            inventoryItem {
              id
              state
              condition
              location { id code }
              authMethod
              authStatus
              needsTroveAuthentication
              troveSerialNumber
              imageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
            }
          }
          sendbackTradeinItems {
            id
            state
            sendbackYpackage { ${YPACKAGE} }
            catalogItem { title sku }
            inventoryItem {
              id
              imageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
            }
          }
        }
      }
    }
  `;

  const data = await execAdmin<
    FetchTradeinsFilters & typeof THUMBNAIL,
    FetchTradeinsResponse
  >(query, { variables: { partnerId, page, ...rest, ...THUMBNAIL } });

  // The resolver ends in `.catch(logger.error)`, which swallows the failure without adding an
  // `errors` entry for execAdmin to throw on — and resolves to winston's logger rather than to
  // nothing, so `tradeins` completes as a present object with every field null. Both halves of
  // this check earn their keep: the `rows` test is the one that fires on that path, and the
  // `!resultPage` test covers an outright null field.
  const resultPage = data?.tradeins;
  if (!resultPage || !Array.isArray(resultPage.rows)) {
    throw new Error('Trade-in search returned no result set');
  }
  // A null count is as malformed as null rows, and tolerating it is not the safe option it looks
  // like: `total: null` makes react-admin render the rows it has while offering no page 2, so
  // the tail of the result set is silently unreachable.
  if (typeof resultPage.count !== 'number') {
    throw new Error('Trade-in search returned no result count');
  }

  // rows is a nullable list of nullable elements, so this narrowing is what lets every caller
  // treat the result as a plain array. Belt-and-braces rather than a live path: a null element
  // only arises when a non-null child errors, which always adds an `errors` entry, and execAdmin
  // throws on that before we get here. So in practice one unreadable row fails the whole page.
  const readable = present<RawTradein>(resultPage.rows);
  const dropped = resultPage.rows.length - readable.length;
  if (dropped > 0) {
    console.error('Trade-in page dropped unreadable rows', {
      dropped,
      received: resultPage.rows.length,
    });
  }

  // Both JSON scalars are parsed at the boundary, not asserted. Every consumer then reads a
  // value that has actually been checked rather than one the record type merely claims — and a
  // shape change surfaces as one console error here instead of as em dashes down the Supplier
  // column, or an <img src="h"> where a placeholder belongs.
  const rows = readable.map(row => ({
    ...row,
    deliveryAddress: parseDeliveryAddress(row.deliveryAddress),
    tradeinItems: present(row.tradeinItems).map(item => ({
      ...item,
      inventoryItem: item.inventoryItem ?
        { ...item.inventoryItem, imageUrls: parseImageUrls(item.inventoryItem.imageUrls) } :
        item.inventoryItem,
    })),
    sendbackTradeinItems: present(row.sendbackTradeinItems).map(item => ({
      ...item,
      inventoryItem: item.inventoryItem ?
        { ...item.inventoryItem, imageUrls: parseImageUrls(item.inventoryItem.imageUrls) } :
        item.inventoryItem,
    })),
  }));

  // `count` is reported as the server sent it. Subtracting the drops looks like it would keep the
  // footer honest, but `count` spans the whole result set while `dropped` is page-local, so the
  // arithmetic breaks navigation instead: react-admin derives hasNextPage from `page * perPage <
  // total`, and shaving the total strands the tail — the same failure the count guard above
  // exists to prevent.
  return { count: resultPage.count, rows };
};

/**
 * Nullable all the way down, as the schema has it: `ConstantsDataAdmin` lists are nullable lists
 * of nullable elements with nullable `code`/`displayName`. The hook's `usableConstants` is what
 * decides whether a payload is good enough to cache, so the honest shape has to reach it rather
 * than being asserted away here.
 */
type RawOptionList = RawTradeinConstants['states'];

interface FetchTradeinConstantsResponse {
  application: {
    constants: {
      tradein: {
        states: RawOptionList;
        tradeinSources: RawOptionList;
        tradeinSendbackStates: RawOptionList;
        tradeinRejectReasons: RawOptionList;
        manualGiftcardReasons: RawOptionList;
      } | null;
      inventoryItem: { allConditions: RawOptionList } | null;
    } | null;
  } | null;
}

/**
 * Filter and dialog options. The names are remapped on the way out — `tradeinSources`,
 * `tradeinSendbackStates` and `manualGiftcardReasons` lose their prefixes — and `conditions`
 * comes from a different branch of the constants tree (`inventoryItem.allConditions`) than the
 * rest, which all hang off `tradein`.
 */
export const fetchTradeinConstants = async (): Promise<RawTradeinConstants> => {
  const query = `
    query fetchTradeinConstants {
      application {
        constants {
          tradein {
            states { code displayName }
            tradeinSources { code displayName }
            tradeinSendbackStates { code displayName }
            tradeinRejectReasons { code displayName }
            manualGiftcardReasons { code displayName }
          }
          inventoryItem {
            allConditions { code displayName }
          }
        }
      }
    }
  `;

  const data = await execAdmin<Record<string, never>, FetchTradeinConstantsResponse>(
    query,
    { variables: {} }
  );

  // Same nullable-envelope hazard as the tradeins query above: without this a null `application`
  // surfaces as "Cannot read properties of null (reading 'constants')", which the hook's catch
  // reports as a failed fetch. Throwing here keeps the message honest; the caller falls back to
  // defaults either way.
  const constants = data?.application?.constants;
  if (!constants) {
    throw new Error('Trade-in constants returned no payload');
  }

  const { tradein, inventoryItem } = constants;
  return {
    states: tradein?.states ?? null,
    sources: tradein?.tradeinSources ?? null,
    sendbackStates: tradein?.tradeinSendbackStates ?? null,
    conditions: inventoryItem?.allConditions ?? null,
    rejectReasons: tradein?.tradeinRejectReasons ?? null,
    giftcardReasons: tradein?.manualGiftcardReasons ?? null,
  };
};
